import { motion, AnimatePresence } from 'framer-motion';
import { getBrowserInstructions, getSystemInstructions } from '../lib/browserPermissions';
import {
  continueLabel,
  getMediaErrorBody,
  getMediaErrorTitle,
  requestedOf,
} from '../lib/mediaErrorCopy';
import type { MediaError } from '../providers/types';

export interface MediaErrorPromptProps {
  isVisible: boolean;
  error: MediaError;
  onContinueWithoutVideo: () => void;
  onChangeDevice?: () => void;
  onRetry?: () => void;
  /** Whether a second camera/mic is available to switch to */
  canChangeDevice?: boolean;
}

/**
 * Modal dialog shown when a local media-capture failure occurs (getUserMedia /
 * createLocalTracks rejection or timeout). Distinct from ReconnectionOverlay,
 * which handles network/room failures.
 *
 * Offers three actionable recoveries, ordered by cause:
 *   - device busy / overconstrained → "Change camera" leads, because another
 *     device is the fix; dropping video is the consolation prize.
 *   - everything else → the "continue" action leads: without video, or in chat
 *     when the microphone is what failed and there is no voice call to keep.
 *   - a closed browser dialog → "Try again" leads: nothing is blocked, and
 *     asking again is exactly the fix.
 *   - otherwise "Try again" comes after them, and only while it can do something:
 *     the caller withholds `onRetry` once the browser is refusing without a
 *     dialog, leaving the instructions as the single, honest way out.
 *
 * "Change camera" is suppressed entirely for both permission types: the answer
 * belongs to the origin, so a second camera fails identically.
 */
export function MediaErrorPrompt({
  isVisible,
  error,
  onContinueWithoutVideo,
  onChangeDevice,
  onRetry,
  canChangeDevice = false,
}: MediaErrorPromptProps) {
  const requested = requestedOf(error);
  const isBlocked = error.type === 'permission-denied';
  const isDismissed = error.type === 'permission-dismissed';
  const retryIsFirstResort = isDismissed && !!onRetry;
  // "Change camera" switches cameras, which is no answer at all when the failure
  // was about the microphone — the voice consultation asks for nothing else.
  const showChangeDevice =
    canChangeDevice && !!onChangeDevice && !isBlocked && !isDismissed && requested !== 'microphone';
  const deviceIsFirstResort =
    showChangeDevice && (error.type === 'device-in-use' || error.type === 'overconstrained');

  const primaryClass =
    'w-full py-3 bg-brand-gold text-white font-medium rounded-lg hover:bg-brand-gold-dark transition-colors';
  const secondaryClass =
    'w-full py-3 border border-brand-gold text-brand-gold font-medium rounded-lg hover:bg-brand-gold/5 transition-colors';

  const changeDeviceButton = showChangeDevice ? (
    <button
      key="change-device"
      onClick={onChangeDevice}
      className={deviceIsFirstResort ? primaryClass : secondaryClass}
    >
      Change camera
    </button>
  ) : null;

  const continueButton = (
    <button
      key="continue"
      onClick={onContinueWithoutVideo}
      className={deviceIsFirstResort || retryIsFirstResort ? secondaryClass : primaryClass}
    >
      {continueLabel(requested)}
    </button>
  );

  const retryButton = onRetry ? (
    <button
      key="retry"
      onClick={onRetry}
      className={
        retryIsFirstResort
          ? primaryClass
          : 'w-full py-3 text-gray-500 text-sm hover:text-gray-700 transition-colors'
      }
    >
      Try again
    </button>
  ) : null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          data-testid="media-error-prompt"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl max-w-md w-full p-6"
          >
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-amber-500"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path
                    d={
                      requested === 'microphone'
                        ? 'M12 2a3 3 0 013 3v6a3 3 0 01-6 0V5a3 3 0 013-3zM5 11a7 7 0 0014 0M12 18v3'
                        : 'M15 10l4.553-2.069A1 1 0 0121 8.82v6.361a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z'
                    }
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <line x1="3" y1="3" x2="21" y2="21" strokeLinecap="round" />
                </svg>
              </div>
            </div>

            <h2 className="text-xl font-serif text-brand-navy text-center mb-2">
              {getMediaErrorTitle(error.type, requested)}
            </h2>

            <p className={`text-gray-600 text-center text-sm ${isBlocked ? 'mb-4' : 'mb-6'}`}>
              {getMediaErrorBody(error.type, requested, { deniedBySystem: error.deniedBySystem })}
            </p>

            {/* A site-level block is only cleared in browser chrome, so the
                instructions are part of the recovery, not a footnote. */}
            {isBlocked && (
              <p
                data-testid="media-error-browser-instructions"
                className="text-gray-500 text-xs leading-relaxed mb-6 pl-3 border-l-2 border-brand-gold/60"
              >
                {error.deniedBySystem
                  ? getSystemInstructions(requested)
                  : getBrowserInstructions(requested)}
              </p>
            )}

            <div className="flex flex-col gap-3">
              {retryIsFirstResort
                ? [retryButton, continueButton]
                : deviceIsFirstResort
                  ? [changeDeviceButton, continueButton, retryButton]
                  : [continueButton, changeDeviceButton, retryButton]}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
